import HHQuizElement from '../element.js'
import { html, unsafeStatic } from 'lit/static-html.js'
import { css } from 'lit'

/**
 * Router link component
 * Renders a link to a specific route
 *
 * @extends
 * @mixes Mixin
 * @property {string} to - The route path
 * @property {string} label - The aria-label for the link
 * @property {boolean} disabled - If true, the link is disabled
 *
 * @example
 * <quiz-router-link to="/step/1">Step 1</quiz-router-link>
 */
class HHQuizRouterLink extends HHQuizElement {
  static properties = {
    to: { type: String },
    label: { type: String },
    disabled: { type: Boolean },
    component: { type: String },
  }

  constructor() {
    super()
    this.component = 'a'
  }

  /**
   * Returns the route object for the current link
   * @returns {Route}
   */
  get route() {
    return this.router.matchAndHydrateRoute(this.to)
  }

  /**
   * Returns the sanitized path for the current link
   * @returns {string}
   */
  get href() {
    return this.route?.sanitizedPath ?? this.to
  }

  /**
   * Returns true if the current link is selected at any level
   */
  get isSelected() {
    return this.router.current?.sanitizedPath.startsWith(this.href)
  }

  /**
   * Returns true if the current link is selected at the current level
   */
  get isChildSelected() {
    return this.router.current?.sanitizedPath === this.href
  }

  render() {
    return html`<${unsafeStatic(this.component)}
      ${this.disabled ? undefined : unsafeStatic(`href="#${this.href}"`)}
      class="hh-quiz-router-link ${this.isSelected ? 'is-selected' : ''} ${this.isChildSelected ? 'is-child-selected' : ''
      }"
      .disabled="${this.disabled}"
      @keydown="${(ev) => ev.stopPropagation()}"
      ><slot></slot
    ></${unsafeStatic(this.component)}>`
  }

  static styles = [
    HHQuizElement.styles,
    css`
      :host {
        cursor: pointer;
      }
      a {
        text-decoration: inherit;
        color: inherit;
        display: inherit;
        cursor: inherit;
      }
    `,
  ]
}

customElements.define('hh-quiz-router-link', HHQuizRouterLink)
